import {
  MenuButton as HeadlessMenuButton,
  Menu,
  MenuItem,
  MenuItems,
  MenuItemsProps,
} from "@headlessui/react"
import { isFocusVisible } from "@react-aria/interactions"
import { Fragment, ReactNode, RefObject } from "react"

import { classNames } from "../../../../lib/classNames"
import { Icon, IconProps, IconSizes } from "../../../Icon"
import { popoverClassName } from "../../overlay/Popover/popoverStyles"
import { Button, ButtonProps, NonTonalButtonMode } from "../Button/Button"
import { IconButton, IconButtonProps } from "../IconButton/IconButton"

interface BaseMenuItemProps {
  label: string
  onClick: () => void
  disabled?: boolean
  icon?: IconProps["icon"]
  dividerAbove?: boolean
}

export type MenuItemProps = BaseMenuItemProps &
  (
    | { trailingText?: string; trailingIcon?: never }
    | { trailingIcon?: IconProps["icon"]; trailingText?: never }
  )

interface MenuButtonItemsProps {
  items: MenuItemProps[]
  header?: ReactNode
  anchor?: MenuItemsProps["anchor"]
  className?: string
  itemClassName?: string
}

export const MenuButtonItems = ({
  items,
  header,
  anchor = { to: "top end", gap: 8 },
  className,
  itemClassName,
}: MenuButtonItemsProps) => (
  <MenuItems
    className={classNames(
      popoverClassName,
      "flex flex-col gap-1 p-1 focus-visible:ring-0",
      className
    )}
    anchor={anchor}
  >
    {header}
    {items.map((item: MenuItemProps) => (
      <Fragment key={item.label}>
        {item.dividerAbove && (
          <div className="border-v2-border-default border-t" />
        )}
        <MenuItem disabled={item.disabled}>
          {({ focus, disabled }) => (
            <div
              onClick={item.onClick}
              className={classNames(
                "text-v2-foreground-default text-body rounded-control flex min-h-8 cursor-pointer items-center gap-x-2 px-2",
                itemClassName,
                focus && "bg-v2-background-surface-strong",
                focus && isFocusVisible() && "ring-v2-border-focus ring-3",
                !disabled && "active:bg-v2-background-surface-stronger",
                disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {item.icon && (
                <Icon
                  icon={item.icon}
                  size={IconSizes.ExtraSmall}
                  className="text-v2-foreground-default-weak shrink-0"
                />
              )}
              {item.label}
              {item.trailingText && (
                <span className="text-v2-foreground-default-weak ml-auto shrink-0 tabular-nums">
                  {item.trailingText}
                </span>
              )}
              {item.trailingIcon && (
                <Icon
                  icon={item.trailingIcon}
                  size={IconSizes.ExtraSmall}
                  className="text-v2-foreground-default-weak ml-auto shrink-0"
                />
              )}
            </div>
          )}
        </MenuItem>
      </Fragment>
    ))}
  </MenuItems>
)

interface MenuButtonProps
  extends Omit<ButtonProps, "onClick" | "mode" | "tone"> {
  mode: NonTonalButtonMode
  ref?: RefObject<HTMLButtonElement | null>
  items: MenuItemProps[]
  /**
   * Icon-only trigger. Never collapses a lone item into a button that fires
   * the action directly, since the icon gives no hint of what would run.
   */
  icon?: IconButtonProps["icon"]
  anchor?: MenuItemsProps["anchor"]
}

export const MenuButton = (props: MenuButtonProps) => {
  const { ref, items, className, icon, anchor, ...buttonProps } = props

  if (items.length === 0) {
    return null
  }

  if (items.length === 1 && icon === undefined) {
    return <Button {...buttonProps} onClick={items[0].onClick} />
  }

  return (
    <Menu as="div" className={classNames(icon && "flex")}>
      {icon ? (
        <HeadlessMenuButton
          as={IconButton}
          ref={ref}
          icon={icon}
          className={triggerClassName}
          {...buttonProps}
        />
      ) : (
        <HeadlessMenuButton
          as={Button}
          ref={ref}
          className={triggerClassName}
          {...buttonProps}
        />
      )}
      <MenuButtonItems
        items={items}
        anchor={anchor}
        className={classNames(icon ? "w-48" : "w-74", className)}
      />
    </Menu>
  )
}

MenuButton.displayName = "MenuButton"

const triggerClassName =
  "focus-visible:ring-0 data-focus:ring-3 data-focus:ring-v2-border-focus"
